import BaseBoosterBehavior, { IBaseBoosterBehaviorPlayMeta } from './BaseBoosterBehavior';
import CardStateHelper from '../CardStateHelper';
import { CardTask } from '../CardTasks';
import { CardType } from '../../../Prototype/Level/Card/CardType';
import { BoosterType } from '../BoosterType';
import { SCORE_TNT_BOOSTER } from '../../../Constants/Score';
import { track, TrackMethod } from '../../../Utility/Tracking/Track';
import { isNearEndGame } from '../../Level/LevelStateHelper';
import Game from '../../../Game';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import { TransactionType } from '../../../Utility/Tracking/DataSet/Transaction';
import { IGameState } from '../../Game/IGameState';
import CardStateModifierHelper from '../CardStateModifierHelper';
import { CardModifierType } from '../../../Prototype/Level/Card/CardModifierType';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';

interface ITNTBoosterBehaviorPlayMeta extends IBaseBoosterBehaviorPlayMeta {
    flipCard?: string;
    ignoreCost?: boolean;
}

const modifiersToPlay: CardModifierType[] = [CardModifierType.pickaxe];

export default class TNTBoosterBehavior extends BaseBoosterBehavior {
    public play(state: IGameState, meta: ITNTBoosterBehaviorPlayMeta): IGameState {
        // If there are not enough target cards, do nothing.
        if (!this.hasEnoughTargetCards(state)) return state;

        let cardsState = {
            ...state.levelState.cardsState,
        };

        // Find all value cards that are face-up and not obscured.
        const targets = this.getTargetCards(state);

        // Using TNT booster restart streak score.
        cardsState = CardStateHelper.cancelStreakScore(cardsState);

        // Discard all.
        const targetScores = targets.map(cardID => {
            cardsState = CardStateHelper.setCardDiscarded(cardsState, cardID);
            const cardBehavior = CardStateHelper.getCardBehavior(cardsState, cardID);
            const modBehaviors = cardBehavior.modifiersOverriding(cardsState, { whitelistTypes: modifiersToPlay });

            state = CardStateHelper.updateGameState(state, cardsState);
            for (let i = 0, n = modBehaviors.length; i < n; i++) {
                state = CardStateModifierHelper.playModifier(
                    state,
                    modBehaviors[i].modifier,
                    modBehaviors[i].cardID
                );
            }
            cardsState = CardStateHelper.getCardsState(state);

            cardsState = CardStateHelper.increaseStreakScore(cardsState, SCORE_TNT_BOOSTER);
            cardsState = CardStateHelper.addTurnScore(cardsState, cardsState.streakScore);
            return {
                cardID: cardID,
                score: cardsState.streakScore,
            };
        });

        // Task for animation.
        cardsState = CardStateHelper.setStateTaskData(cardsState, {
            task: CardTask.TNT,
            cards: targets,
            targets: targetScores,
            flipCard: meta?.flipCard,
        });

        targets.forEach(cardID => {
            cardsState = CardStateModifierHelper.onCardDiscarded(cardsState, cardID);
        });

        // Check if cards need to be flipped.
        cardsState = CardStateHelper.checkCardsObscured(cardsState);

        state = {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: cardsState,
            },
        };

        if (meta?.ignoreCost) {
            return state;
        } else {
            return this.consumeItemOrCoins(state);
        }
    }

    public getTargetCards(state: IGameState): string[] {
        const cardsState = { ...state.levelState.cardsState };

        return cardsState.inPlay.filter(cardID => {
            const behavior = CardStateHelper.getCardBehavior(cardsState, cardID);
            return (
                (cardsState.cards[cardID].type === CardType.value || cardsState.cards[cardID].type === CardType.gold) &&
                !behavior.isBlocked(cardsState)
            );
        });
    }

    public hasEnoughTargetCards(state: IGameState): boolean {
        return this.getTargetCards(state).length > 0;
    }

    protected getPrice(state: IGameState): number {
        return state.levelState.boosterPrices[BoosterType.TNT];
    }

    protected getInventoryItem(state: IGameState): number {
        return state.playerState.inventory[BoosterType.TNT];
    }

    protected spendInventoryItem(state: IGameState): IGameState {
        track('transactionType', TransactionType.INVENTORY);

        // TODO: use InventoryStateHelper.consumeInventory instead
        return {
            ...state,
            playerState: InventoryStateHelper.setBoosterTNT(
                state.playerState,
                state.playerState.inventory[BoosterType.TNT] - 1,
                { queueTask: false }
            ),
        };
    }

    protected spendCoins(state: IGameState): IGameState {
        super.trackCoinsSpent(state, 'tMassRemoveCoins');
        state = super.spendCoins(state);

        return {
            ...state,
            levelState: {
                ...state.levelState,
                boosterUses: {
                    ...state.levelState.boosterUses,
                    [BoosterType.TNT]: state.levelState.boosterUses[BoosterType.TNT] + 1,
                },
            },
        };
    }

    protected consumeItemOrCoins(state: IGameState): IGameState {
        Game.sdk.gameTracking(FBTrackingEvent.TapOnCanister);
        track('mMassRemoveUsed', 1, TrackMethod.ADD);
        track('tMassRemoveUsed', 1, TrackMethod.ADD);

        if (isNearEndGame(state)) {
            track('mNumBoostersUsedLossAversion', 1, TrackMethod.ADD);
        }

        return super.consumeItemOrCoins(state);
    }
}
